/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { When, Switch, Case, Default } from "react-if";
import {
  Box,
  Tab,
  Text,
  theme,
  FlexContainer,
  SVGIcon,
  Tabs,
} from "@nordcloud/gnui";
import { useCurrency } from "~/hooks";
import { FormatMoney, getIcon, isNil, isNotNil, Maybe } from "~/tools";
import { CloudAccountsResponse, CloudSummaryTabData } from "../types";
import { getProviderName } from "../utils";
import { CloudAccountTab } from "./CloudAccountTab";
import { MovedCosts } from "./MovedCosts";

type Props = {
  activeTab: number;
  setActiveTab: (index: number) => void;
  tabs: CloudSummaryTabData[];
  openFiltersSidebar: () => void;
  searchInput?: Maybe<string>;
  onSetSearchInput: (input: string) => void;
  isLoadingCloudAccounts: boolean;
  data?: Maybe<CloudAccountsResponse>;
  onClear: () => void;
};

export function CloudAccountsTabs({
  activeTab,
  setActiveTab,
  tabs,
  openFiltersSidebar,
  isLoadingCloudAccounts,
  searchInput,
  onSetSearchInput,
  data,
}: Props) {
  const { currency } = useCurrency();

  const generatedTabs = useMemo(() => {
    return tabs.map(({ name, cost, count, movedToK8s, movedToOc }) => (
      <Tab
        key={name}
        label={
          <span>
            <FlexContainer
              justifyContent="space-between"
              mb={theme.spacing.spacing01}
              css={{ height: "2rem" }}
            >
              <FlexContainer columnGap={theme.spacing.spacing03}>
                <SVGIcon name={getIcon(name)} />
                <Text mb={0} weight="medium">
                  {getProviderName(name)}
                </Text>
              </FlexContainer>
              <When condition={isNotNil(count)}>
                <Box
                  boxStyle="lightGrey"
                  innerSpacing="spacing01"
                  css={{ width: "auto" }}
                >
                  <Text
                    m={0}
                    size="sm"
                    p={`0.125rem ${theme.spacing.spacing02}`}
                  >
                    {count}
                  </Text>
                </Box>
              </When>
            </FlexContainer>
            <FlexContainer justifyContent="space-between">
              <Switch>
                <Case condition={isNil(cost)}>
                  <Text mb={0} size="sm" color={theme.color.text.text02}>
                    Not Onboarded
                  </Text>
                </Case>
                <Case condition={cost === "N/A"}>
                  <Text mb={0} size="sm" color={theme.color.text.text02}>
                    Cost not available
                  </Text>
                </Case>
                <Default>
                  <Text mb={0} size="sm" weight="medium">
                    <FormatMoney value={cost} currency={currency} />
                  </Text>
                </Default>
              </Switch>
              <MovedCosts
                currency={currency}
                movedToK8s={movedToK8s}
                movedToOc={movedToOc}
              />
            </FlexContainer>
          </span>
        }
      >
        <CloudAccountTab
          openFiltersSidebar={openFiltersSidebar}
          isLoading={isLoadingCloudAccounts}
          searchInput={searchInput}
          data={data}
          movedToK8s={movedToK8s}
          movedToOc={movedToOc}
          onSetSearchInput={onSetSearchInput}
        />
      </Tab>
    ));
  }, [
    tabs,
    currency,
    searchInput,
    isLoadingCloudAccounts,
    data,
    openFiltersSidebar,
    onSetSearchInput,
  ]);

  return (
    <Tabs step={activeTab} handleTab={setActiveTab}>
      {generatedTabs}
    </Tabs>
  );
}
